import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract language from URL path
  const getLanguageFromPath = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const firstSegment = pathSegments[0];
    return ['ba', 'en', 'fr', 'es', 'it', 'ru', 'ar', 'de', 'pt', 'tr', 'zh'].includes(firstSegment) ? firstSegment : 'ba';
  };

  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Check URL first, then localStorage, then default to 'ba'
    const urlLang = getLanguageFromPath();
    const savedLang = localStorage.getItem('preferred_language');
    return urlLang || savedLang || 'ba';
  });

  // Get path without language prefix
  const getPathWithoutLanguage = (path) => {
    const segments = path.split('/').filter(Boolean);
    if (['ba', 'en', 'fr', 'es', 'it', 'ru', 'ar', 'de', 'pt', 'tr', 'zh', 'hi', 'ur'].includes(segments[0])) {
      return '/' + segments.slice(1).join('/');
    }
    return path;
  };

  // Get path with language prefix
  const getPathWithLanguage = (path, lang) => {
    const cleanPath = getPathWithoutLanguage(path);
    return `/${lang}${cleanPath === '/' ? '' : cleanPath}`;
  };

  // Change language and update URL
  const changeLanguage = (newLanguage) => {
    if (newLanguage === currentLanguage) return;
    
    setCurrentLanguage(newLanguage);
    localStorage.setItem('preferred_language', newLanguage);
    
    // Update URL with new language
    const currentPathWithoutLang = getPathWithoutLanguage(location.pathname);
    const newPath = getPathWithLanguage(currentPathWithoutLang, newLanguage);
    navigate(newPath + location.search + location.hash, { replace: true });
  };

  // Update current language when URL changes
  useEffect(() => {
    const urlLang = getLanguageFromPath();
    if (urlLang !== currentLanguage) {
      setCurrentLanguage(urlLang);
      localStorage.setItem('preferred_language', urlLang);
    }
  }, [location.pathname]);

  // Redirect to language-prefixed URL if no language in path
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const hasLanguagePrefix = ['ba', 'en', 'fr', 'es', 'it', 'ru', 'ar', 'de', 'pt', 'tr', 'zh', 'hi', 'ur'].includes(pathSegments[0]);
    
    if (!hasLanguagePrefix && location.pathname !== '/' && !(currentLanguage === 'ba' && pathSegments[0] === 'p')) {
      const newPath = getPathWithLanguage(location.pathname, currentLanguage);
      navigate(newPath + location.search + location.hash, { replace: true });
    } else if (!hasLanguagePrefix && location.pathname === '/') {
      // For root path, redirect to language version
      navigate(`/${currentLanguage}` + location.search + location.hash, { replace: true });
    }
  }, [location.pathname, currentLanguage, navigate]);

  const value = {
    currentLanguage,
    changeLanguage,
    getPathWithLanguage,
    getPathWithoutLanguage,
    availableLanguages: ['ba', 'en', 'fr', 'es', 'it', 'ru', 'ar', 'de', 'pt', 'tr', 'zh', 'hi', 'ur']
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
